import { useRef, useState } from 'react'
import { motion, useInView, AnimatePresence } from 'framer-motion'
import { fadeInUp, fadeInLeft, fadeInRight } from '../hooks/useScrollAnimation'

const FORMFILL_ENDPOINT = 'https://api.seraj.live/formfill/xwvwrnwb'

const socials = [
  {
    label: 'GitHub',
    handle: '@its-seraj',
    href: 'https://github.com/its-seraj',
    color: '#e6edf3',
    icon: (
      <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
        <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z" />
      </svg>
    ),
  },
  {
    label: 'LinkedIn',
    handle: 'linkedin.com/in/its-seraj',
    href: 'https://www.linkedin.com/in/its-seraj/',
    color: '#0077b5',
    icon: (
      <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
      </svg>
    ),
  },
  {
    label: 'Twitter / X',
    handle: '@its_seraj',
    href: 'https://twitter.com/its_seraj',
    color: '#1d9bf0',
    icon: (
      <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
      </svg>
    ),
  },
  {
    label: 'Email',
    handle: 'serajkhan48522@gmail.com',
    href: 'mailto:serajkhan48522@gmail.com',
    color: '#00ff41',
    icon: (
      <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="2" y="4" width="20" height="16" rx="2" />
        <path d="m22 7-8.97 5.7a1.94 1.94 0 01-2.06 0L2 7" />
      </svg>
    ),
  },
]

export default function Contact() {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: '0px 0px -80px 0px' })

  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' })
  const [status, setStatus] = useState('idle') // idle | sending | sent | error
  const [focused, setFocused] = useState(null)
  const [errorMsg, setErrorMsg] = useState('')

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setStatus('sending')
    setErrorMsg('')
    try {
      const res = await fetch(FORMFILL_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(formData),
      })
      if (res.ok) {
        setStatus('sent')
        setFormData({ name: '', email: '', subject: '', message: '' })
      } else {
        const data = await res.json()
        setErrorMsg(data?.errors?.[0]?.message || 'Something went wrong.')
        setStatus('error')
      }
    } catch {
      setErrorMsg('Network error — check your connection.')
      setStatus('error')
    }
  }

  return (
    <section id="contact" className="py-24 relative">
      <div className="max-w-7xl mx-auto px-6 lg:px-8" ref={ref}>
        {/* Header */}
        <motion.div
          variants={fadeInUp}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          className="mb-16"
        >
          <p className="font-mono text-[#00ff41] text-sm mb-2">// 05. contact</p>
          <h2 className="section-title">
            Get In <span>Touch</span>
          </h2>
          <div className="h-0.5 w-24 bg-gradient-to-r from-[#00ff41] to-transparent mt-3" />

          {/* Quote */}
          <motion.div
            variants={fadeInUp}
            className="mt-8 inline-flex items-start gap-3 glass-card border border-[#00ff41]/20 px-5 py-4 rounded-xl max-w-xl"
          >
            <span className="text-[#00ff41] font-mono text-lg leading-none mt-0.5 shrink-0">"</span>
            <p className="font-mono text-sm text-[#8b949e] italic leading-relaxed">
              I like building systems that make money,{' '}
              <span className="text-[#e6edf3] not-italic font-semibold">not just code that runs.</span>
            </p>
            <span className="text-[#00ff41] font-mono text-lg leading-none mt-auto shrink-0">"</span>
          </motion.div>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 xl:gap-20">
          {/* Left: Info */}
          <motion.div
            variants={fadeInLeft}
            initial="hidden"
            animate={isInView ? 'visible' : 'hidden'}
          >
            {/* Status badge */}
            <div className="flex items-center gap-3 mb-8 glass-card border border-[#00ff41]/30 inline-flex px-4 py-2.5 rounded-full">
              <div className="relative">
                <div className="w-2.5 h-2.5 rounded-full bg-[#00ff41]" />
                <div className="absolute inset-0 w-2.5 h-2.5 rounded-full bg-[#00ff41] animate-ping opacity-60" />
              </div>
              <span className="font-mono text-sm text-[#00ff41]">Available for opportunities</span>
            </div>

            <h3 className="text-2xl font-semibold text-[#e6edf3] mb-4">
              Let's build something great together
            </h3>
            <p className="text-[#8b949e] leading-relaxed mb-8">
              Whether you have a challenging technical problem to solve, need a senior
              engineer to lead your team, or want to discuss a new product idea — my
              inbox is always open. I respond to all messages within 24 hours.
            </p>

            {/* What I'm looking for */}
            <div className="glass-card border border-[#30363d] p-5 rounded-xl mb-8">
              <p className="font-mono text-xs text-[#6e7681] mb-3">// currently interested in</p>
              <ul className="space-y-2">
                {[
                  'Senior Software Engineer roles',
                  'AI/LLM backend engineering',
                  'B2B SaaS platform development',
                  'Open source collaboration',
                ].map((item) => (
                  <li key={item} className="flex items-center gap-2 text-sm text-[#8b949e]">
                    <span className="text-[#00ff41]">▸</span> {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Socials */}
            <div className="space-y-3">
              {socials.map((social) => (
                <motion.a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 group p-3 rounded-lg border border-transparent hover:border-[#30363d] hover:bg-[#161b22] transition-all duration-200"
                  whileHover={{ x: 4 }}
                >
                  <div
                    className="w-9 h-9 rounded-lg flex items-center justify-center border border-[#30363d] group-hover:border-opacity-60 transition-all"
                    style={{ color: social.color }}
                  >
                    {social.icon}
                  </div>
                  <div>
                    <p className="font-mono text-xs text-[#6e7681]">{social.label}</p>
                    <p className="font-mono text-sm text-[#e6edf3] group-hover:text-[#00ff41] transition-colors">
                      {social.handle}
                    </p>
                  </div>
                  <div className="ml-auto text-[#30363d] group-hover:text-[#00ff41] transition-colors">
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M7 17l10-10M7 7h10v10" />
                    </svg>
                  </div>
                </motion.a>
              ))}
            </div>
          </motion.div>

          {/* Right: Form */}
          <motion.div
            variants={fadeInRight}
            initial="hidden"
            animate={isInView ? 'visible' : 'hidden'}
          >
            <div className="glass-card border border-[#30363d] rounded-xl overflow-hidden">
              {/* Terminal title bar */}
              <div className="flex items-center gap-2 px-4 py-3 border-b border-[#30363d] bg-[#161b22]">
                <div className="flex gap-2">
                  <div className="w-3 h-3 rounded-full bg-[#ff5f57]" />
                  <div className="w-3 h-3 rounded-full bg-[#febc2e]" />
                  <div className="w-3 h-3 rounded-full bg-[#28c840]" />
                </div>
                <span className="flex-1 text-center font-mono text-xs text-[#6e7681]">
                  send_message.sh
                </span>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-5">
                {/* Name + Email row */}
                <div className="grid sm:grid-cols-2 gap-4">
                  {['name', 'email'].map((field) => (
                    <div key={field} className="relative">
                      <label
                        htmlFor={field}
                        className={`font-mono text-xs transition-all duration-200 block mb-1.5 ${
                          focused === field ? 'text-[#00ff41]' : 'text-[#6e7681]'
                        }`}
                      >
                        <span className="text-[#00ff41]">$</span> {field}
                      </label>
                      <input
                        id={field}
                        name={field}
                        type={field === 'email' ? 'email' : 'text'}
                        value={formData[field]}
                        onChange={handleChange}
                        onFocus={() => setFocused(field)}
                        onBlur={() => setFocused(null)}
                        required
                        placeholder={field === 'email' ? 'you@example.com' : 'Your name'}
                        className="input-terminal"
                      />
                    </div>
                  ))}
                </div>

                {/* Subject */}
                <div>
                  <label
                    htmlFor="subject"
                    className={`font-mono text-xs transition-all duration-200 block mb-1.5 ${
                      focused === 'subject' ? 'text-[#00ff41]' : 'text-[#6e7681]'
                    }`}
                  >
                    <span className="text-[#00ff41]">$</span> subject
                  </label>
                  <input
                    id="subject"
                    name="subject"
                    type="text"
                    value={formData.subject}
                    onChange={handleChange}
                    onFocus={() => setFocused('subject')}
                    onBlur={() => setFocused(null)}
                    required
                    placeholder="What's this about?"
                    className="input-terminal"
                  />
                </div>

                {/* Message */}
                <div>
                  <label
                    htmlFor="message"
                    className={`font-mono text-xs transition-all duration-200 block mb-1.5 ${
                      focused === 'message' ? 'text-[#00ff41]' : 'text-[#6e7681]'
                    }`}
                  >
                    <span className="text-[#00ff41]">$</span> message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={5}
                    value={formData.message}
                    onChange={handleChange}
                    onFocus={() => setFocused('message')}
                    onBlur={() => setFocused(null)}
                    required
                    placeholder="Tell me about your project or opportunity..."
                    className="input-terminal resize-none"
                  />
                </div>

                {/* Submit */}
                <motion.button
                  type="submit"
                  disabled={status === 'sending' || status === 'sent'}
                  onClick={status === 'error' ? () => setStatus('idle') : undefined}
                  className="w-full btn-primary justify-center py-3 disabled:opacity-60 disabled:cursor-not-allowed"
                  whileHover={status === 'idle' || status === 'error' ? { scale: 1.01 } : {}}
                  whileTap={status === 'idle' || status === 'error' ? { scale: 0.99 } : {}}
                >
                  {status === 'idle' && (
                    <>
                      <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="22" y1="2" x2="11" y2="13" />
                        <polygon points="22 2 15 22 11 13 2 9 22 2" />
                      </svg>
                      execute send_message()
                    </>
                  )}
                  {status === 'sending' && (
                    <>
                      <svg className="animate-spin" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 12a9 9 0 11-6.219-8.56" />
                      </svg>
                      transmitting...
                    </>
                  )}
                  {status === 'sent' && (
                    <>
                      <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      message delivered!
                    </>
                  )}
                  {status === 'error' && (
                    <>
                      <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="22" y1="2" x2="11" y2="13" />
                        <polygon points="22 2 15 22 11 13 2 9 22 2" />
                      </svg>
                      retry send_message()
                    </>
                  )}
                </motion.button>

                {/* Success flash */}
                <AnimatePresence>
                  {status === 'sent' && (
                    <motion.div
                      initial={{ opacity: 0, y: 12, scale: 0.97 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -8, scale: 0.97 }}
                      transition={{ duration: 0.4, ease: 'easeOut' }}
                      className="rounded-lg border border-[#00ff41]/40 bg-[#00ff41]/5 p-4"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-2 h-2 rounded-full bg-[#00ff41] animate-pulse" />
                        <span className="font-mono text-xs text-[#00ff41] font-semibold tracking-wide">
                          PROCESS EXITED WITH CODE 0
                        </span>
                      </div>
                      <div className="font-mono text-xs text-[#8b949e] space-y-1 pl-4">
                        <p><span className="text-[#00ff41]">✓</span> Message transmitted successfully</p>
                        <p><span className="text-[#00ff41]">✓</span> Delivered to serajkhan48522@gmail.com</p>
                        <p><span className="text-[#6e7681]">→</span> Expected response time: <span className="text-[#e6edf3]">&lt; 24 hours</span></p>
                      </div>
                    </motion.div>
                  )}

                  {/* Error flash */}
                  {status === 'error' && (
                    <motion.div
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="rounded-lg border border-[#ff5f57]/40 bg-[#ff5f57]/5 p-4"
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <div className="w-2 h-2 rounded-full bg-[#ff5f57]" />
                        <span className="font-mono text-xs text-[#ff5f57] font-semibold tracking-wide">
                          TRANSMISSION FAILED
                        </span>
                      </div>
                      <p className="font-mono text-xs text-[#8b949e] pl-4">
                        {errorMsg || 'Something went wrong — please try again.'}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </form>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}
